import React, { useState, useCallback, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Select from "react-select";
import { useDropzone } from "react-dropzone";
import { UnoStoreHooks } from "@simulanisindia/ui";
import axios from "axios";
import { SSO_URL } from "../env";
import { AddImageIcon, CancelIcon, CloseIcon } from "../assets/Icons";
import { CREATE_NEW_APPLICATION, UPDATE_APPLICATION, UPLOAD_APPLICATION_LOGO } from "../apiClient/apiEndpoints";
import apiClient from "../apiClient";

const { useUnoSelector } = UnoStoreHooks;
const defaultFormValues = {
  name: "",
  description: "",
  status: true,
};

const accessTypeOptions = [
  { value: "PRIVATE", label: "Private" },
  { value: "PUBLIC", label: "Public" },
];

const AddApplication = ({
  applicationList = [],
  applicationModal,
  setApplicationModal,
  editMode,
  applicationData,
  refresh,
}) => {
  const userData = useUnoSelector((state) => state.user);
  const [uploadedIcon, setUploadedIcon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accessType, setAccessType] = useState({ value: "PRIVATE", label: "Private" });
  const [maskingApplication, setMaskingApplication] = useState(null);
  const [maskingApplicationList, setMaskingApplicationList] = useState([]);
  const [formValues, setFormValues] = useState(defaultFormValues);

  useEffect(() => {
    let _list = applicationList.map((app) => ({ value: app.uuid, label: app.name }));
    setMaskingApplicationList(_list);
  }, [applicationList]);

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    const reader = new FileReader();

    reader.onload = () => {
      setUploadedIcon(reader.result);
    };

    reader.readAsDataURL(file);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("randomize", true);

    const uploadedImageData = await handleImageUpload(formData);
    setUploadedIcon(uploadedImageData?.url);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: "image/*",
    multiple: false,
  });

  const handleCancelUpload = (event) => {
    event.stopPropagation();
    setUploadedIcon(null);
  };

  const handleImageUpload = async (payload) => {
    try {
      const res = await axios.post(`${SSO_URL}${UPLOAD_APPLICATION_LOGO}/logo`, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${userData.token}`,
        },
      });
      return res.data;
    } catch (error) {
      console.log(error);
    }
  };

  const handleSave = async () => {
    if (loading) return;
    try {
      setLoading(true);
      const payload = {
        name: formValues.name,
        description: formValues.description,
        icon: uploadedIcon,
        status: formValues.status,
        accessType: accessType.value,
        parentId: maskingApplication?.value || null,
      };
      if (!editMode) {
        await apiClient.post(`${SSO_URL}${CREATE_NEW_APPLICATION}`, payload);
      } else {
        await apiClient.post(`${SSO_URL}${UPDATE_APPLICATION}/${applicationData.uuid}`, payload);
      }
      refresh();
      handleCancelClick();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    setFormValues(defaultFormValues);
    setUploadedIcon("");
    setApplicationModal(false);
  };

  useEffect(() => {
    if (editMode) {
      setFormValues({
        name: applicationData?.name,
        description: applicationData?.description,
        status: applicationData?.active,
      });
      setUploadedIcon(applicationData.icon);
      setAccessType(accessTypeOptions.find((item) => item.value === applicationData.accessType));
      if (applicationData?.parent?.uuid && applicationData?.parent?.name) {
        setMaskingApplication({
          value: applicationData?.parent?.uuid || null,
          label: applicationData?.parent?.name || null,
        });
      }
    }
  }, [editMode, applicationData]);

  return (
    <Modal show={applicationModal} centered onHide={handleCancelClick}>
      <Modal.Header className="modalheader">
        {editMode ? "Edit Application" : "Add New Application"}
        <span onClick={handleCancelClick}>
          <CloseIcon />
        </span>
      </Modal.Header>
      <Modal.Body className="pb-0">
        <div className="row">
          <div className="image-formbox mt-0">
            <div {...getRootProps()} className="dropbox">
              <input {...getInputProps()} />
              {uploadedIcon ? (
                <div className="uploaded-container">
                  <img src={uploadedIcon} alt="Uploaded" className="uploaded-image" />
                  <button onClick={handleCancelUpload} className="cancel-button">
                    <CancelIcon />
                  </button>
                </div>
              ) : (
                <div className="placeholder">
                  <AddImageIcon />
                </div>
              )}
            </div>
            <div className="d-flex align-items-start flex-column justify-content-start">
              <p>Application Icon</p>
              <p className="little-info">Recommended Format* PNG</p>
            </div>
          </div>
          <div className="col-12 standardinput mt-3">
            <label>
              Application Name <span>*</span>
            </label>
            <input
              autoComplete="off"
              name="machineName"
              id="machineName"
              className="input form-control"
              type="text"
              value={formValues.name}
              onChange={(e) => setFormValues((prev) => ({ ...prev, name: e.target.value }))}
            />
          </div>
          <div className="col-12 standardinput mt-3">
            <label>Application Description</label>
            <textarea
              autoComplete="off"
              name="machineName"
              id="machineName"
              className="input form-control"
              type="text"
              value={formValues.description}
              onChange={(e) => setFormValues((prev) => ({ ...prev, description: e.target.value }))}
            />
          </div>

          <div className="col-12 standardinput mt-3">
            <label className="mb-2">Access Type</label>
            <div className="forselect-input">
              <Select
                theme={(theme) => ({
                  ...theme,
                  borderRadius: 5,
                  colors: {
                    ...theme.colors,
                    primary50: "#ca373c50",
                    primary25: "#ca373c50",
                    primary: "#ca373c70",
                    danger: "#000000",
                    neutral30: "#00000050",
                  },
                })}
                options={accessTypeOptions}
                value={accessType}
                onChange={(value) => setAccessType(value)}
              />
            </div>
          </div>

          <div className="col-12 standardinput mt-3">
            <label className="mb-2">Parent Application</label>

            <div className="forselect-input">
              <Select
                isClearable
                theme={(theme) => ({
                  ...theme,
                  borderRadius: 5,
                  colors: {
                    ...theme.colors,
                    primary50: "#ca373c50",
                    primary25: "#ca373c50",
                    primary: "#ca373c70",
                    danger: "#000000",
                    neutral30: "#00000050",
                  },
                })}
                isDisabled={editMode}
                options={maskingApplicationList}
                value={maskingApplication}
                onChange={(value) => setMaskingApplication(value)}
              />
            </div>
          </div>

          <div className="col-12 standardinput toggle mt-3">
            <p>Status :</p>
            <label className="switch">
              <input
                type="checkbox"
                checked={formValues.status}
                onChange={() => setFormValues((prev) => ({ ...prev, status: !prev.status }))}
              />
              <span className="slider"></span>
            </label>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="footerbtn">
        <button className="cancel" onClick={handleCancelClick}>
          Cancel
        </button>
        {!loading ? (
          <button className="save" onClick={handleSave}>
            {editMode ? "Update" : "Save"}
          </button>
        ) : (
          <button className="loading save">
            <svg viewBox="25 25 50 50">
              <circle r="20" cy="50" cx="50"></circle>
            </svg>
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AddApplication;
